import React, { useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ListIcon, HomeIcon, UsersIcon, EyeIcon, FileTextIcon, LayoutDashboardIcon, HelpCircleIcon, MenuIcon, XIcon, UserCircleIcon, LogOutIcon, ChevronRightIcon } from 'lucide-react';
import { RentalFlowTracker } from './RentalFlowTracker';
import { useRental } from '../../contexts/RentalContext';
export function RentalLayout() {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const {
    getStats
  } = useRental();
  const stats = getStats();
  // Navigation menu items with badges
  const menuItems = [{
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboardIcon,
    path: '/inchiriere',
    gradient: 'from-blue-500 to-indigo-600',
    color: 'text-blue-600'
  }, {
    id: 'prospecting',
    label: 'Liste de prospectare',
    icon: ListIcon,
    path: '/inchiriere/liste-prospectare',
    gradient: 'from-blue-500 to-indigo-600',
    color: 'text-blue-600',
    badge: stats.totalProspectingLeads > 0 ? {
      count: stats.totalProspectingLeads,
      type: 'urgent'
    } : undefined
  }, {
    id: 'leads',
    label: 'Leaduri',
    icon: UsersIcon,
    path: '/inchiriere/leaduri',
    gradient: 'from-orange-500 to-red-600',
    color: 'text-orange-600',
    badge: stats.activeTenantLeads > 0 ? {
      count: stats.activeTenantLeads,
      type: 'info'
    } : undefined
  }, {
    id: 'viewings',
    label: 'Vizionări',
    icon: EyeIcon,
    path: '/inchiriere/vizionari',
    gradient: 'from-blue-500 to-indigo-600',
    color: 'text-blue-600',
    badge: stats.todayViewings > 0 ? {
      count: stats.todayViewings,
      type: 'today'
    } : undefined
  }, {
    id: 'contracts',
    label: 'Contracte',
    icon: FileTextIcon,
    path: '/inchiriere/contracte',
    gradient: 'from-purple-500 to-pink-600',
    color: 'text-purple-600'
  }, {
    id: 'help',
    label: 'Ajutor & Proceduri',
    icon: HelpCircleIcon,
    path: '/inchiriere/ajutor',
    gradient: 'from-slate-500 to-slate-600',
    color: 'text-slate-600'
  }];
  const isActive = (path: string) => {
    if (path === '/inchiriere') {
      return location.pathname === '/inchiriere' || location.pathname === '/inchiriere/';
    }
    return location.pathname.startsWith(path);
  };
  const getBadgeColor = (type: string) => {
    switch (type) {
      case 'urgent':
        return 'bg-red-500 text-white animate-pulse';
      case 'new':
        return 'bg-emerald-500 text-white';
      case 'today':
        return 'bg-orange-500 text-white';
      case 'info':
        return 'bg-blue-500 text-white';
      default:
        return 'bg-slate-500 text-white';
    }
  };
  return <div className="w-full min-h-screen bg-slate-50 flex" data-id="element-5334">
      {/* Mobile Header with Hamburger Menu */}
      <div className="lg:hidden fixed top-16 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-slate-200 z-30 px-4 py-3 flex items-center justify-between shadow-sm" data-id="element-5335">
        <button onClick={() => setSidebarOpen(true)} className="flex items-center space-x-2 text-slate-700 hover:text-blue-600 transition-colors" data-id="element-5336">
          <MenuIcon className="w-6 h-6" data-id="element-5337" />
          <span className="font-semibold text-sm" data-id="element-5338">Meniu</span>
        </button>

        <div className="flex items-center space-x-2" data-id="element-5339">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-lg flex items-center justify-center shadow-md" data-id="element-5340">
            <HomeIcon className="w-4 h-4 text-white" data-id="element-5341" />
          </div>
          <span className="font-bold text-[#132C53] text-sm" data-id="element-5342">pro.rental</span>
        </div>
      </div>

      {/* Sidebar - Desktop */}
      <aside className="hidden lg:block w-72 bg-white border-r border-slate-200 fixed left-0 top-16 bottom-0 overflow-y-auto z-30 shadow-lg" data-id="element-5343">
        {/* Navigation Menu */}
        <nav className="p-4 pb-32 space-y-6" data-id="element-5344">
          <div className="px-4 mb-2" data-id="element-5345">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-5346">
              Meniu Principal
            </h3>
          </div>
          <ul className="space-y-2" data-id="element-5347">
            {menuItems.map(item => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return <li key={item.id} data-id="element-5348">
                  <Link to={item.path} className={`
                      group flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-300 relative overflow-hidden
                      ${active ? `bg-gradient-to-r ${item.gradient} text-white shadow-lg shadow-blue-500/20` : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}
                    `} data-id="element-5349">
                    <div className={`
                      p-2 rounded-lg transition-colors
                      ${active ? 'bg-white/20' : 'bg-slate-100 group-hover:bg-white'}
                    `} data-id="element-5350">
                      <Icon className={`w-5 h-5 ${active ? 'text-white' : item.color}`} data-id="element-5351" />
                    </div>

                    <span className="font-medium text-sm flex-1" data-id="element-5352">
                      {item.label}
                    </span>

                    {item.badge && <span className={`px-2 py-0.5 rounded-full text-xs font-bold shadow-sm ${getBadgeColor(item.badge.type)}`} data-id="element-5353">
                        {item.badge.count}
                      </span>}

                    {active && <motion.div layoutId="activeSidebar" className="absolute right-2" initial={{
                  opacity: 0,
                  x: -10
                }} animate={{
                  opacity: 1,
                  x: 0
                }} data-id="element-5354">
                        <ChevronRightIcon className="w-4 h-4 text-white/80" data-id="element-5355" />
                      </motion.div>}
                  </Link>
                </li>;
          })}
          </ul>
        </nav>

        {/* User Section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-100 bg-slate-50/50 backdrop-blur-sm" data-id="element-5356">
          <div className="flex items-center space-x-3 mb-3 p-2 rounded-xl hover:bg-white transition-colors cursor-pointer" data-id="element-5357">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-md ring-2 ring-white" data-id="element-5358">
              <UserCircleIcon className="w-6 h-6 text-white" data-id="element-5359" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-5360">
              <p className="text-sm font-bold text-slate-900 truncate" data-id="element-5361">
                Agent Demo
              </p>
              <p className="text-xs text-slate-500 truncate" data-id="element-5362">
                agent@cautlocuinta.ro
              </p>
            </div>
          </div>
          <button className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-white text-slate-700 rounded-xl text-sm font-semibold hover:bg-red-50 hover:text-red-600 hover:shadow-md transition-all border border-slate-200 hover:border-red-100" data-id="element-5363">
            <LogOutIcon className="w-4 h-4" data-id="element-5364" />
            <span data-id="element-5365">Deconectare</span>
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence data-id="element-5366">
        {sidebarOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setSidebarOpen(false)} className="lg:hidden fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50" data-id="element-5367" />
            <motion.aside initial={{
          x: -300
        }} animate={{
          x: 0
        }} exit={{
          x: -300
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="lg:hidden w-72 bg-white fixed left-0 top-0 h-screen overflow-y-auto z-50 shadow-2xl" data-id="element-5368">
              {/* Mobile Sidebar Content */}
              <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50" data-id="element-5369">
                <div className="flex items-center space-x-3" data-id="element-5370">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg" data-id="element-5371">
                    <HomeIcon className="w-6 h-6 text-white" data-id="element-5372" />
                  </div>
                  <div data-id="element-5373">
                    <h2 className="text-lg font-bold text-[#132C53]" data-id="element-5374">
                      pro.rental
                    </h2>
                    <p className="text-xs text-slate-500" data-id="element-5375">Dashboard Agent</p>
                  </div>
                </div>
                <button onClick={() => setSidebarOpen(false)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-500 hover:text-slate-700" data-id="element-5376">
                  <XIcon className="w-6 h-6" data-id="element-5377" />
                </button>
              </div>

              <nav className="p-4 pb-32 space-y-6" data-id="element-5378">
                <div className="px-4 mb-2" data-id="element-5379">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-5380">
                    Meniu Principal
                  </h3>
                </div>
                <ul className="space-y-2" data-id="element-5381">
                  {menuItems.map(item => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return <li key={item.id} data-id="element-5382">
                        <Link to={item.path} onClick={() => setSidebarOpen(false)} className={`
                            flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-300 relative overflow-hidden
                            ${active ? `bg-gradient-to-r ${item.gradient} text-white shadow-lg` : 'text-slate-600 hover:bg-slate-50'}
                          `} data-id="element-5383">
                          <div className={`
                            p-2 rounded-lg
                            ${active ? 'bg-white/20' : 'bg-slate-100'}
                          `} data-id="element-5384">
                            <Icon className={`w-5 h-5 ${active ? 'text-white' : item.color}`} data-id="element-5385" />
                          </div>

                          <span className="font-medium text-sm flex-1" data-id="element-5386">
                            {item.label}
                          </span>

                          {item.badge && <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${getBadgeColor(item.badge.type)}`} data-id="element-5387">
                              {item.badge.count}
                            </span>}
                        </Link>
                      </li>;
              })}
                </ul>
              </nav>

              <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-100 bg-slate-50" data-id="element-5388">
                <div className="flex items-center space-x-3 mb-3 p-2 bg-white rounded-xl shadow-sm border border-slate-100" data-id="element-5389">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-md" data-id="element-5390">
                    <UserCircleIcon className="w-6 h-6 text-white" data-id="element-5391" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-5392">
                    <p className="text-sm font-bold text-slate-900 truncate" data-id="element-5393">
                      Agent Demo
                    </p>
                    <p className="text-xs text-slate-500 truncate" data-id="element-5394">
                      agent@cautlocuinta.ro
                    </p>
                  </div>
                </div>
                <button className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-white text-slate-700 rounded-xl text-sm font-semibold hover:bg-red-50 hover:text-red-600 hover:shadow-md transition-all border border-slate-200" data-id="element-5395">
                  <LogOutIcon className="w-4 h-4" data-id="element-5396" />
                  <span data-id="element-5397">Deconectare</span>
                </button>
              </div>
            </motion.aside>
          </>}
      </AnimatePresence>

      {/* Main Content Area */}
      <div className="flex-1 lg:ml-72 w-full min-h-screen flex flex-col" data-id="element-5398">
        {/* Top spacing for fixed header */}
        <div className="lg:mt-16 pt-[52px] lg:pt-0 flex-1 flex flex-col" data-id="element-5399">
          {/* Flow Tracker */}
          <RentalFlowTracker data-id="element-5400" />

          {/* Content Area */}
          <main className="flex-1 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50" data-id="element-5401">
            <Outlet data-id="element-5402" />
          </main>
        </div>
      </div>
    </div>;
}